import { useMemo } from 'react'

import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { CartContainer } from '../../../brunswick/containers/cart'
import { EmbroideryService } from '../../../containers/EmbroideryService'
import { EmbroideryModalService } from '../../../containers/EmbroideryService/EmbroideryModalService'
import { Output as AddToBagOutput } from '../../../lib/hooks/useBag'
import { hashPrefixed } from '../../../lib/utils'

interface Props {
  resetInvalidBagClickStatus: AddToBagOutput['resetInvalidBagClickStatus']
  /** The discount messaging that should be displayed at the bottom of the embroidery summary */
  discountMessage?: string
}

export function useViewModel({ resetInvalidBagClickStatus, discountMessage }: Props) {
  const {
    clearSelectedEmbroideryFormOptions,
    confirmAddEmbroidery,
    selectedEmbroideryFormOptions: options,
    toggleConfirmAddEmbroidery,
  } = EmbroideryService.useContainer()

  const { openEmbroideryModal } = EmbroideryModalService.useContainer()
  const {
    embroidery: { summary: strings },
    cart: cartText,
  } = useTranslation()

  const { productColorHex } = EmbroideryService.useContainer()

  const { cart } = CartContainer.useContainer()

  const backgroundColor = hashPrefixed(productColorHex)

  const handleToggleConfirmAddEmbroidery = () => {
    resetInvalidBagClickStatus()
    toggleConfirmAddEmbroidery()
  }

  const setupSkuInCart = useMemo(() => {
    if (cart?.items) {
      return Object.values(cart.items).some(
        item =>
          item.properties?._is_logo_setup === 'true' && item.properties?._logo_id === options.logoId
      )
    }
    return false
  }, [cart, options.logoId])

  const embroideryOptionPrice = useMemo(() => {
    let price = options.embroideryItemVariant?.price ?? 0
    if (options.embroiderySetupVariant && (options.setupFile || !setupSkuInCart)) {
      price += options.embroiderySetupVariant.price
    }
    return price
  }, [options, setupSkuInCart])

  const handleOpenEmbroideryModal = (event: React.MouseEvent<HTMLElement>) => {
    openEmbroideryModal({ triggerElement: event.currentTarget })
  }

  return {
    clearSelectedEmbroideryFormOptions,
    confirmAddEmbroidery,
    editEmbroideryText: strings.editEmbroidery,
    embroideryOptionPrice: embroideryOptionPrice,
    openEmbroideryModal: handleOpenEmbroideryModal,
    toggleAddEmbroideryText: confirmAddEmbroidery
      ? `${cartText.embroideryItemLabel}:`
      : strings.toggleAddEmbroidery,
    toggleConfirmAddEmbroidery: handleToggleConfirmAddEmbroidery,
    textSneakPeekProps: {
      backgroundColor,
      textColor: options?.textColor,
      textFirstLine: options?.textFirstLine,
      textFont: options?.textFont,
      textPlacement: options?.textPlacement,
      textSecondLine: options?.textSecondLine,
    },
    iconSneakPeekProps: {
      backgroundColor,
      iconImageSource: options?.iconImageSource,
      iconInitials: options?.iconInitials,
      iconInitialsColor: options?.iconInitialsColor,
      iconDesignName: options?.iconDesignName,
      iconPlacement: options?.iconPlacement,
    },
    dtcLogoSneakPeekProps: {
      backgroundColor,
      imgSrc: options?.logoPreview,
      logoName: options?.logoName,
      logoPlacement: options?.logoPlacement,
      logoColor: options?.logoColor,
      logoNote: options?.logoNote,
      hasLogoSetup: options?.embroiderySetupVariant && (options?.setupFile || !setupSkuInCart),
    },
    logoSneakPeekProps: {
      backgroundColor,
      imgSrc: options?.logoImageSource,
      logoName: options?.logoName,
      logoPlacement: options.logoPlacement,
    },
    embroideryDiscountMessage: discountMessage,
    canEditEmbroidery: true,
  }
}
